import {useCallback, useEffect, useState} from 'react';
import type {
	CompetitorAnalysisResult,
	CompetitorAnalysisRun,
	StartCompetitorAnalysisRequest,
	StartCompetitorAnalysisResponse,
} from '@ozon-ai/sellmonitor/contracts';
import {api} from '../../../api/client';

export type AnalysisPeriodChoice = '7' | '30' | '90' | 'custom';

function errorText(err: unknown, fallback: string): string {
	return err instanceof Error ? err.message : fallback;
}

async function waitForRun(runId: string): Promise<CompetitorAnalysisRun> {
	for (let attempt = 0; attempt < 90; attempt += 1) {
		const run = await api.get<CompetitorAnalysisRun>(
			`/sellmonitor/competitor-analysis?id=${encodeURIComponent(runId)}`,
		);
		if (run.status !== 'running' && run.status !== 'created') return run;
		await new Promise((resolve) => setTimeout(resolve, 2000));
	}
	throw new Error('Анализ занимает больше обычного. Обновите вкладку позже.');
}

export function useCompetitorAnalysis(offerId: string) {
	const [result, setResult] = useState<CompetitorAnalysisResult | null>(null);
	const [run, setRun] = useState<CompetitorAnalysisRun | null>(null);
	const [loading, setLoading] = useState(true);
	const [running, setRunning] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		const [current, runs] = await Promise.all([
			api.get<CompetitorAnalysisResult | null>(
				`/sellmonitor/competitor-analysis-current?offerId=${encodeURIComponent(offerId)}`,
			),
			api.get<CompetitorAnalysisRun[]>(
				`/sellmonitor/competitor-analyses?offerId=${encodeURIComponent(offerId)}`,
			),
		]);
		setResult(current);
		setRun(runs[0] ?? null);
		return runs[0];
	}, [offerId]);

	useEffect(() => {
		if (!offerId) return;
		let cancelled = false;
		setLoading(true);
		load()
			.then(async (latest) => {
				if (cancelled || !latest) return;
				if (latest.status === 'running' || latest.status === 'created') {
					setRunning(true);
					const finished = await waitForRun(latest.id);
					if (!cancelled) {
						setRun(finished);
						setResult(finished.result ?? null);
					}
				}
			})
			.catch((err: unknown) => {
				if (!cancelled) setError(errorText(err, 'Не удалось загрузить анализ'));
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
					setRunning(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [offerId, load]);

	const start = useCallback(async (input: Omit<StartCompetitorAnalysisRequest, 'offerId' | 'force'>) => {
		setRunning(true);
		setError(null);
		try {
			const started = await api.post<StartCompetitorAnalysisResponse>('/sellmonitor/competitor-analyses', {
				offerId,
				force: true,
				...input,
			});
			const finished = started.status === 'running' || started.status === 'created'
				? await waitForRun(started.runId)
				: await api.get<CompetitorAnalysisRun>(`/sellmonitor/competitor-analysis?id=${encodeURIComponent(started.runId)}`);
			setRun(finished);
			setResult(finished.result ?? null);
			if (finished.status === 'failed') setError(finished.error || 'Анализ не выполнен');
		} catch (err) {
			setError(errorText(err, 'Не удалось обновить анализ'));
		} finally {
			setRunning(false);
		}
	}, [offerId]);

	return {result, run, loading, running, error, start};
}
